
import { useEffect, useCallback, useState } from 'react';

type UnloadCheckFunction = () => boolean;

/**
 * Hook to manage the beforeunload event and detect unsaved changes
 */
export const useBeforeUnload = () => {
  const [checkFunctions, setCheckFunctions] = useState<UnloadCheckFunction[]>([]);
  
  // Function to register a new check for unsaved changes
  const registerUnsavedChangesCheck = useCallback((checkFn: UnloadCheckFunction) => {
    setCheckFunctions(prev => [...prev, checkFn]);
    
    return () => {
      setCheckFunctions(prev => prev.filter(fn => fn !== checkFn));
    };
  }, []);
  
  // Function to check if there are any unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return checkFunctions.some(fn => fn());
  }, [checkFunctions]);
  
  // Set up beforeunload event listener
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        // Standard way to show the browser's "unsaved changes" dialog
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  
  return { hasUnsavedChanges, registerUnsavedChangesCheck };
};

export default useBeforeUnload;
